import React, { useState, useRef, useEffect } from 'react'

const JobPhotosTab = ({ formData, onChange }) => {
  const [previews, setPreviews] = useState([])
  const [photosToRemove, setPhotosToRemove] = useState([])
  const fileInputRef = useRef(null)

  // Load existing photos from formData on mount
  useEffect(() => {
    const previewsToLoad = []

    // Load existing photos
    if (formData.existing_job_photos && Array.isArray(formData.existing_job_photos)) {
      const existingPhotos = formData.existing_job_photos.map(photo => ({
        id: photo.id,
        url: photo.url,
        name: photo.filename,
        isExisting: true
      }))
      previewsToLoad.push(...existingPhotos)
    }

    // Load newly added photos that were persisted to formData
    if (formData.job_photos && Array.isArray(formData.job_photos)) {
      const newPhotos = formData.job_photos.map(file => ({
        id: crypto.randomUUID(),
        file: file,
        name: file.name,
        url: URL.createObjectURL(file),
        isExisting: false
      }))
      previewsToLoad.push(...newPhotos)
    }

    setPreviews(previewsToLoad)
  }, [])

  // Sync new photos and removal list to formData
  useEffect(() => {
    const newFiles = previews
      .filter(p => !p.isExisting)
      .map(p => p.file)

    onChange('job_photos', newFiles)
    onChange('job_photos_to_remove', photosToRemove)
  }, [previews, photosToRemove])

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files)

    // Validate files are images
    const imageFiles = files.filter(file => file.type.startsWith('image/'))

    if (imageFiles.length !== files.length) {
      alert('Please select only image files')
    }

    const newPreviews = imageFiles.map(file => ({
      id: crypto.randomUUID(),
      file: file,
      name: file.name,
      url: URL.createObjectURL(file),
      isExisting: false
    }))

    setPreviews(prev => [...prev, ...newPreviews])

    event.target.value = ''
  }

  const handleRemoveImage = (previewId, isExisting) => {
    if (isExisting) {
      // Track existing photo for removal
      setPhotosToRemove(prev => [...prev, previewId])
    }

    setPreviews(prev => {
      const updated = prev.filter(p => p.id !== previewId)
      const removed = prev.find(p => p.id === previewId)
      if (removed && !removed.isExisting) {
        URL.revokeObjectURL(removed.url)
      }
      return updated
    })
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className="space-y-10 py-4">
      {previews.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {previews.map((preview) => (
            <div key={preview.id} className="relative group">
              <div className="aspect-square rounded overflow-hidden bg-gray-100 border-2 border-gray-200">
                <img
                  src={preview.url}
                  alt={preview.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <button
                type="button"
                onClick={() => handleRemoveImage(preview.id, preview.isExisting)}
                className="absolute top-2 right-2 bg-danger text-white rounded-full w-6 h-6 flex items-center justify-center"
                aria-label={`Remove ${preview.name}`}
              >
                <i className="fa-regular fa-xmark text-sm" aria-hidden="true"></i>
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-center">
        <button
          type="button"
          onClick={handleUploadClick}
          className="flex flex-col items-center justify-between gap-2"
        >
          <i className="fa-regular fa-sharp fa-camera text-2xl" aria-hidden="true"></i>
          <span className="text-lg">Take Photos</span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          aria-label="Select photos to upload"
        />
      </div>
    </div>
  )
}

export default JobPhotosTab
